import { Outlet, createFileRoute, notFound } from "@tanstack/react-router";
import {
  DEFAULT_LANGUAGE,
  LOCALES,
} from "@modules/shared/localization/const/language";
import i18next from "@modules/shared/localization/i18n";
import { Navbar } from "@/modules/Navbar/Navbar";
import { Footer } from "@/modules/shared/Footer/Footer";
import { NotFound } from "@/defaults/NotFound";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

const PUBLIC_ORIGIN = "https://postolarvarga.hr";

const LOCALE_HREFLANGS: Record<
  (typeof LOCALES)[number],
  ReadonlyArray<string>
> = {
  hr: ["hr", "hr-HR"],
  de: ["de", "de-DE", "de-AT", "de-CH"],
};

export const Route = createFileRoute("/$locale/_layout")({
  notFoundComponent: () => <NotFound />,
  ssr: true,
  async beforeLoad({ params }) {
    const locale = params.locale;

    if (LOCALES.includes(locale as (typeof LOCALES)[number])) {
      if (i18next.language !== locale) {
        await i18next.changeLanguage(locale);
      }

      const countryVariables = getCountryVariables(locale);

      return {
        locale,
        ...countryVariables,
      };
    } else {
      throw notFound();
    }
  },
  head: ({ params, matches }) => ({
    links: getLanguageSeoLinks(
      params.locale,
      matches.at(-1)?.pathname ?? `/${params.locale}`,
    ),
  }),
  component: () => (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      {/* shrink-0: a page with min-h-screen would otherwise shrink to one
          screen and run under the footer. relative: main is the scroll
          container, so sr-only (absolute) elements must be placed in it,
          not in the document, or they make the whole page scroll. */}
      <main className="relative flex flex-1 flex-col overflow-auto [&>*]:shrink-0">
        <Outlet />
        <Footer />
      </main>
    </div>
  ),
});

function getLanguageSeoLinks(locale: string, pathname: string) {
  // No trailing slash: /hr/ redirects to /hr, so a canonical of /hr/ points
  // Google at a redirect.
  const pathWithoutLocale = pathname
    .replace(/^\/(hr|de)(?=\/|$)/, "")
    .replace(/\/$/, "");

  return [
    {
      rel: "canonical",
      href: `${PUBLIC_ORIGIN}/${locale}${pathWithoutLocale}`,
    },
    ...LOCALES.flatMap((locale) =>
      LOCALE_HREFLANGS[locale].map((hrefLang) => ({
        rel: "alternate",
        hrefLang,
        href: `${PUBLIC_ORIGIN}/${locale}${pathWithoutLocale}`,
      })),
    ),
    {
      rel: "alternate",
      hrefLang: "x-default",
      href: `${PUBLIC_ORIGIN}/${DEFAULT_LANGUAGE}${pathWithoutLocale}`,
    },
  ];
}
